import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { Legal } from 'src/smoothr-web-app-core/models/Legal';
import { RepositoryService } from 'src/smoothr-web-app-core/services/repository/repository.service';
import { NavigationService } from './navigation.service';

export type LegalType = 'imprint' | 'privacy' | 'terms';

// the endpoint also returns per-language versions, which the core Legal model doesn't declare yet
type LegalResponse = Legal & {
	translations?: Partial<Record<LegalType, Record<string, string>>>;
};

@Injectable({
	providedIn: 'root',
})
export class LegalService {
	private cache = new Map<string, Promise<LegalResponse | null>>();

	constructor(
		private repository: RepositoryService,
		private navigationService: NavigationService,
		private modalCtrl: ModalController,
		private translate: TranslateService
	) {}

	get venueId(): string | null {
		return this.repository._venue?._id ?? this.repository.getStoredVenueId() ?? null;
	}

	getLegal(venueId: string = this.venueId): Promise<LegalResponse | null> {
		if (!venueId) {
			return Promise.resolve(null);
		}
		if (!this.cache.has(venueId)) {
			const request = Api.getLegal(venueId)
				.then(response => response.data ?? null)
				.catch(e => {
					console.error('Error while getting legal information', e);
					this.cache.delete(venueId);
					return null;
				});
			this.cache.set(venueId, request);
		}
		return this.cache.get(venueId);
	}

	document(legal: LegalResponse | null, type: LegalType): string {
		return legal?.translations?.[type]?.[this.translate.currentLang] || legal?.[type] || '';
	}

	async getHtml(type: LegalType): Promise<string> {
		const legal = await this.getLegal();
		return legal?.type === 'html' ? this.document(legal, type) : '';
	}

	async open(type: LegalType) {
		const legal = await this.getLegal();
		if (legal?.type === 'pdf') {
			window.open(this.document(legal, type), '_blank');
			return;
		}
		// ModalNavigationGuard blocks navigation while a modal (e.g. checkout) is open
		while (await this.modalCtrl.getTop()) {
			await this.modalCtrl.dismiss();
		}
		switch (type) {
			case 'imprint':
				return this.navigationService.imprint();
			case 'privacy':
				return this.navigationService.privacy();
			case 'terms':
				return this.navigationService.tos();
		}
	}
}
